import { FilePicker } from "@/components/FilePicker";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, getRouteApi, useNavigate } from "@tanstack/react-router";
import { ApiError, exportMembers, importMembers, searchMembers, type MemberSummary } from "@/api/client";
import { useSession } from "@/auth/session";
import { DataTable, type Column } from "@/components/DataTable";
import { PagedFooter } from "@/components/Pagination";
import { saveBlob } from "@/components/download";
import { EmptyState, ErrorBanner, ToolbarButton, WorkspaceHeader } from "@/components/ui";
import { ScrollArea } from "@/components/ScrollArea";
import { pageParams } from "@/routes/paging";

const membersApi = getRouteApi("/staff/members");

const columns: Column<MemberSummary>[] = [
  {
    key: "member",
    header: "Member",
    primary: true,
    cell: (member) => (
      <Link
        to="/members/$memberId"
        params={{ memberId: member.id }}
        className="inline-flex min-h-10 items-center font-medium text-zinc-950 underline-offset-2 hover:underline md:inline md:min-h-0"
      >
        {member.full_name}
      </Link>
    ),
  },
  {
    key: "number",
    header: "Membership no.",
    cell: (member) => <span className="font-mono text-xs text-zinc-600">{member.membership_no ?? "-"}</span>,
  },
  { key: "email", header: "Email", cell: (member) => <span className="text-zinc-700">{member.email ?? "-"}</span> },
  {
    key: "certificates",
    header: "Certificates",
    cell: (member) => <span className="text-zinc-700">{member.certificate_count}</span>,
  },
];

export function MemberSearch() {
  const search = membersApi.useSearch();
  const { q, page, page_size } = search;
  const { can } = useSession();
  const navigate = useNavigate();

  const results = useQuery({
    queryKey: ["members", q ?? "", page, page_size],
    queryFn: () => searchMembers({ q, ...pageParams(search) }),
  });
  const items = results.data?.items ?? [];

  const exportCsv = useMutation({
    mutationFn: async () => saveBlob(await exportMembers(q), "members.csv"),
  });

  function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const text = String(new FormData(e.currentTarget).get("q") ?? "").trim();
    void navigate({ to: "/members", search: { q: text || undefined, page_size } });
  }

  return (
    <section className="space-y-5">
      <WorkspaceHeader
        title="Members"
        detail="Search by name, membership number, email, external ref, or program"
        action={
          <ToolbarButton disabled={exportCsv.isPending} onClick={() => exportCsv.mutate()}>
            {exportCsv.isPending ? "Exporting..." : "Export CSV"}
          </ToolbarButton>
        }
      />

      <form role="search" onSubmit={submit} className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center">
        {/* key resets the uncontrolled field when back/forward changes ?q= */}
        <input
          key={q ?? ""}
          name="q"
          aria-label="Search members"
          defaultValue={q ?? ""}
          placeholder="e.g. Ada Okafor, A00012, Associate 2026"
          className="flex-1 rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm md:min-w-72 outline-none focus:border-brand focus:ring-1 focus:ring-brand"
        />
        <button type="submit" className="min-h-10 rounded-md bg-brand-strong px-3 py-2 text-sm font-medium text-white hover:bg-brand-stronger md:min-h-0">
          Search
        </button>
      </form>

      <ErrorBanner error={results.error ?? exportCsv.error} />
      {can("member.import") ? <MemberImport /> : null}

      <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
        {results.isLoading ? (
          <EmptyState title="Searching" detail="Looking up members." />
        ) : items.length === 0 ? (
          <EmptyState title="No members found" detail="Try a different name or number, or import members." />
        ) : (
          <div className="p-3 md:p-0">
            <DataTable rows={items} columns={columns} rowKey={(member) => member.id} label="Members" tableClassName="min-w-[720px]" />
          </div>
        )}
        <PagedFooter data={results.data} page={page} pageSize={page_size} />
      </div>
    </section>
  );
}

function MemberImport() {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const upload = useMutation({
    mutationFn: (chosen: File) => importMembers(chosen),
    onSuccess: async () => {
      // Clear the picker so the same file cannot be imported twice by accident.
      setFile(null);
      await queryClient.invalidateQueries({ queryKey: ["members"] });
    },
  });
  const problems = upload.error instanceof ApiError ? upload.error.details : [];

  return (
    <div className="space-y-3 rounded-lg border border-zinc-200 bg-white p-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-64 flex-1">
          <FilePicker
            label="Member file"
            accept=".csv,.xlsx"
            extensions={[".csv", ".xlsx"]}
            typeError="Only .csv or .xlsx files can be imported"
            maxBytes={25 << 20}
            hint="CSV or Excel (.xlsx), up to 25 MB"
            value={file}
            onChange={(next) => {
              setFile(next);
              upload.reset();
            }}
          />
        </div>
        <ToolbarButton disabled={!file || upload.isPending} onClick={() => file && upload.mutate(file)}>
          {upload.isPending ? "Importing..." : "Import"}
        </ToolbarButton>
      </div>
      <p className="text-xs text-zinc-500">
        Columns: name (required), email, phone, membership_no, external_ref. If any row has a problem or matches an
        existing member, nothing is imported.{" "}
        <a href="/samples/members-import-sample.csv" download className="font-medium text-brand-ink underline hover:text-brand-strong">
          Download a sample CSV
        </a>
      </p>
      {upload.data ? <p className="text-sm font-medium text-emerald-700">Imported {upload.data.created} members.</p> : null}
      <ErrorBanner error={upload.error} />
      {problems.length > 0 ? (
        <ScrollArea label="Import problems" className="max-h-72 overflow-y-auto rounded-md border border-zinc-200">
          <table className="w-full text-left text-xs">
            <thead className="sticky top-0 border-b border-zinc-200 bg-zinc-50 text-zinc-500">
              <tr>
                <th className="px-3 py-2 font-medium">Row</th>
                <th className="px-3 py-2 font-medium">Column</th>
                <th className="px-3 py-2 font-medium">Problem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {problems.map((problem) => (
                <tr key={`${problem.row}-${problem.field}-${problem.issue}`}>
                  <td className="px-3 py-2 text-zinc-500">{problem.row}</td>
                  <td className="px-3 py-2 font-mono text-zinc-700">{problem.field}</td>
                  <td className="px-3 py-2 text-zinc-800">{problem.issue}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollArea>
      ) : null}
    </div>
  );
}
